<script setup lang="ts">
import { useTranslations } from '~/composables/translations';
import { useTracker } from '~/composables/tracker';

import type { Country } from '~/models/me';

import { onMounted, Ref, ref } from 'vue';

import { VuInputDate, VuSelect } from '@ds/platformkit/components';
import { useCountryStore } from '~/stores/countries';

// I18N
const { $i18n: $i18nCS } = useTranslations('components/ordering/country_selector');
const { $i18n: $i18nO } = useTranslations('ordering');

// PROPS
interface Props {
  countries?: any; // TODO Country[]
  selectedCountry?: Country;
  value?: string;
  disabled?: boolean;
  deliveryDate?: string;
  displayDeliveryDate?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  countries: () => [],
  selectedCountry: undefined,
  disabled: false,
  value: '',
  deliveryDate: '',
  displayDeliveryDate: true,
});

// EMITS
const emit = defineEmits(['selectCountry', 'updateDeliveryDate']);

// VARIABLES
const countriesStore = useCountryStore();
const tracker = useTracker();
const selectedCountryIsocode: Ref<string | undefined> = ref(props.selectedCountry?.isocode3);

// METHODS
const updateDeliveryDate = (event: any) => {
  emit('updateDeliveryDate', `${event.toLocaleDateString('en-US')} 11:59:59 PM`);
};

// MOUNTED
onMounted(() => {
  // We are unable to tell if the country returned by the service is the US default or the user is just in the US
  tracker?.event('estimated_country_auto_computed', 'country_selector', 'PASSIVE', [
    '',
    '',
    '',
    '',
    '',
    props.value,
  ]);
});
</script>
<template>
  <div
    class="flex flex-row align-items-center flex-grow-1 grid-gap-10px deliver-to country-selector-container"
  >
    <label for="country-selector-name" class="ma-0px" style="flex: none">
      {{ $i18nCS('deliveryTo') }}
    </label>
    <template v-if="props.countries.length === 0">
      <div class="loading-gray-box shimmer" style="height: 39px; width: 100%" />
    </template>
    <vu-select
      v-else
      id="country-selector-name"
      class="flex-grow-1"
      :hide-placeholder-option="true"
      :options="
        props.countries.map((country: any) => ({ label: country.name, value: country.isocode3 }))
      "
      size="5"
      :model-value="selectedCountryIsocode || countriesStore.estimatedUserCountry?.isocode3"
      @update:model-value="
        ($event: string) => {
          selectedCountryIsocode = $event;
          emit('selectCountry', selectedCountryIsocode);
        }
      "
    />
    <!-- Expected Deliveries -->
    <label
      v-if="props.displayDeliveryDate"
      style="flex: 0 0 auto; font-weight: inherit"
      for="date-selector"
    >
      {{ $i18nO('select_expectedDelivery') }}
    </label>
    <vu-input-date
      v-if="props.displayDeliveryDate"
      id="date-selector"
      name="date-selector"
      class="data-cell4 flex expected-delivery-input date-picker"
      :value="props.deliveryDate && new Date(props.deliveryDate)"
      :min="new Date()"
      :placeholder="$i18nO('funnel_selectDeliveryDate')"
      @input="updateDeliveryDate"
    />
  </div>
</template>

<style scoped lang="scss">
@use '/assets/scss/skeletons';

label {
  font-weight: 500;
}

.flex:deep(.form-group) {
  margin-bottom: 0;
  flex-grow: 1;
}
</style>
